// CreateUserGroupDrawer.tsx
import React, { useContext, useEffect } from "react";
import { Drawer, Form, Input, Select, Button, Space, message } from "antd";
import { UIAssetFilterDTO, UserDTO, UserGroupCreateDTO, UserViewDTO } from "../../backendapi/requests";
import { useUserGroupServicePostBackendv2Usergroup } from "../../backendapi/queries";
import { ConfigContext } from "../../contexts/ConfigContext";

const { Option } = Select;

interface CreateUserGroupDrawerProps {
  visible: boolean;
  onClose: () => void;
  onSuccess: () => void;
  availableUsers: UserViewDTO[];
  availableFilters: UIAssetFilterDTO[];
}

interface UserGroupFormData {
  name: string;
  clientId: number;
  users: number[];
  filters: number[];
}

const CreateUserGroup: React.FC<CreateUserGroupDrawerProps> = ({ visible, onClose, onSuccess, availableUsers, availableFilters }) => {
  const { currentClient } = useContext(ConfigContext);

  const [form] = Form.useForm();
  const [loading, setLoading] = React.useState(false);

  const { mutateAsync: createUserGroup } = useUserGroupServicePostBackendv2Usergroup();

  useEffect(() => {
    if (!visible) {
      form.resetFields();
    }
  }, [visible, form]);

  const handleSubmit = async () => {
    setLoading(true);
    try {
      const values = form.getFieldsValue(true) as UserGroupFormData;

      const formData = {
        clientId: currentClient.id,
        name: values.name,
        filters: availableFilters.filter(f => values.filters.includes(f.id)),
        users: availableUsers.filter(f => values.users.includes(f.id)),
      } as UserGroupCreateDTO

      await createUserGroup({ requestBody: formData });

      message.success("User group created successfully");
      form.resetFields();
      onSuccess();
      onClose();
    } catch (error) {
      message.error("Failed to create user group");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Drawer
      title="Create User Group"
      placement="right"
      size={500}
      onClose={onClose}
      open={visible}
      extra={
        <Space>
          <Button onClick={onClose}>Cancel</Button>
          <Button type="primary" onClick={() => form.submit()} loading={loading}>
            Create
          </Button>
        </Space>
      }>
      <Form form={form} layout="vertical" onFinish={handleSubmit}>
        <Form.Item
          label="Name"
          name="name"
          rules={[
            { required: true, message: "Please enter a name" },
            { min: 3, message: "Name must be at least 3 characters" },
          ]}>
          <Input placeholder="Enter user group name" />
        </Form.Item>

         <Form.Item label="Users" name="users">
            <Select mode="multiple" placeholder="Select users" options={availableUsers.map((x) => ({ label: x.username, value: x.id }))} showSearch />
        </Form.Item>

        <Form.Item label="Filters" name="filters">
            <Select mode="multiple" placeholder="Select filters" options={availableFilters.map((x) => ({ label: x.title, value: x.id }))} showSearch />
        </Form.Item>
      </Form>
    </Drawer>
  );
};

export default CreateUserGroup;
